/**
 * ConnectionBanner - Displays connection status when backend is unavailable.
 *
 * Shows a non-intrusive banner at the top of the page when:
 * - Backend is disconnected
 * - Connection is recovering (retrying)
 *
 * Only shows "Connection restored" message if there was an actual
 * disconnection - NOT on normal page load/refresh.
 */

import { useEffect, useState, useRef } from "react";
import { WifiOff, RefreshCw, CheckCircle } from "lucide-react";
import {
  type ConnectionState,
  onConnectionChange,
  attemptRecovery,
  getConnectionStatus,
} from "../../services/connection";

interface ConnectionStatus {
  state: ConnectionState;
  lastCheck: Date | null;
  consecutiveFailures: number;
  lastError: string | null;
}

export function ConnectionBanner() {
  const [status, setStatus] = useState<ConnectionStatus>(getConnectionStatus());
  const [isRetrying, setIsRetrying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Track if there was ever a real disconnection (not just initial connecting state)
  const hadDisconnection = useRef(false);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const unsubscribe = onConnectionChange((newStatus) => {
      // Mark if we ever had a real disconnection or recovery state
      // (not just the initial CONNECTING state on page load)
      if (
        !isInitialLoad.current &&
        (newStatus.state === "DISCONNECTED" || newStatus.state === "RECOVERING")
      ) {
        hadDisconnection.current = true;
      }

      // After first state update, we're past initial load
      if (isInitialLoad.current && newStatus.state === "CONNECTED") {
        isInitialLoad.current = false;
      } else if (newStatus.state !== "CONNECTING") {
        isInitialLoad.current = false;
      }

      // Only show success message if:
      // 1. We had a real disconnection before
      // 2. Previous state was not CONNECTED
      // 3. New state IS CONNECTED
      if (
        hadDisconnection.current &&
        status.state !== "CONNECTED" &&
        newStatus.state === "CONNECTED"
      ) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          hadDisconnection.current = false; // Reset for next time
        }, 2000);
      }

      setStatus(newStatus);
    });

    return unsubscribe;
  }, [status.state]);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await attemptRecovery();
    } finally {
      setIsRetrying(false);
    }
  };

  // Don't show banner when connected (unless showing success animation)
  if (status.state === "CONNECTED" && !showSuccess) {
    return null;
  }

  // Don't show "connecting" on initial page load - only show if there was a problem
  if (status.state === "CONNECTING" && isInitialLoad.current) {
    return null;
  }

  // Success animation - only show if there was actual disconnection
  if (showSuccess) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-green-600 text-white px-4 py-2 flex items-center justify-center gap-2 animate-slide-down">
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">Connection restored</span>
      </div>
    );
  }

  const isDisconnected = status.state === "DISCONNECTED";
  const isRecovering =
    status.state === "RECOVERING" || status.state === "CONNECTING";

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 flex items-center justify-center gap-3 ${
        isDisconnected ? "bg-red-600" : "bg-amber-500"
      } text-white`}
    >
      {isRecovering ? (
        <RefreshCw className="w-4 h-4 animate-spin" />
      ) : (
        <WifiOff className="w-4 h-4" />
      )}

      <span className="text-sm font-medium">
        {isDisconnected
          ? "Unable to connect to server"
          : "Connecting to server..."}
      </span>

      {status.lastError && (
        <span className="text-xs opacity-80 hidden sm:inline">
          ({status.lastError})
        </span>
      )}

      {isDisconnected && (
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="ml-2 px-3 py-1 text-xs font-medium bg-white/20 hover:bg-white/30 rounded transition-colors disabled:opacity-50"
        >
          {isRetrying ? "Retrying..." : "Retry"}
        </button>
      )}
    </div>
  );
}

export default ConnectionBanner;
